const HARD_CAP = 1e12;
const HARD_CAP_LABEL = '999B+';

const SUFFIXES = [
  { divider: 1e3, suffix: 'K' },
  { divider: 1e6, suffix: 'M' },
  { divider: 1e9, suffix: 'B' },
] as const;

const groupFormatter = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 0,
});

const stripZeros = (value: string) => String(Number(value));

const formatCompact = (value: number) => {
  for (const { divider, suffix } of SUFFIXES) {
    const rounded = Number((value / divider).toPrecision(4));
    // rounding can bump the value into the next suffix (999,950 -> 1000K -> 1M)
    if (rounded < 1000) {
      return `${stripZeros(String(rounded))}${suffix}`;
    }
  }
  return HARD_CAP_LABEL;
};

const formatPositive = (value: number): string => {
  if (value >= HARD_CAP) {
    return HARD_CAP_LABEL;
  }
  // when rounding bumps a value into the next tier (0.99999 -> 1),
  // the rounded value is formatted by that tier instead
  if (value < 1) {
    const result = value.toFixed(4);
    return Number(result) < 1
      ? stripZeros(result)
      : formatPositive(Number(result));
  }
  if (value < 1000) {
    const result = value.toPrecision(5);
    return Number(result) < 1000
      ? stripZeros(result)
      : formatPositive(Number(result));
  }
  if (value < 10000) {
    const result = Math.round(value);
    return result < 10000
      ? groupFormatter.format(result)
      : formatPositive(result);
  }
  return formatCompact(value);
};

/**
 * Formats an amount for compact display:
 * - 0 – 0.9999…      fixed 4 decimals, trailing zeros stripped (0.1235)
 * - 1 – 999.99…      5 significant digits, trailing zeros stripped (123.46)
 * - 1,000 – 9,999    rounded to whole, comma-grouped (9,999)
 * - 10,000 – 999.5B  4 significant digits + K / M / B (12.35K)
 * - ≥ 1T             hard cap "999B+"
 */
export const formatAmount = (amount: number): string => {
  if (Number.isNaN(amount)) {
    return '0';
  }
  if (amount < 0) {
    const result = formatPositive(-amount);
    return result === '0' ? result : `-${result}`;
  }
  return formatPositive(amount);
};
